import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Snackbar,
  Alert,
} from '@mui/material';
import FilledButton from './FilledButton';
import OutlinedButton from './OutlinedButton';
import AddIcon from '@mui/icons-material/Add';
import api, { fetchTopicProposals } from '../services/api';

const capitalize = (str) =>
  str ? str.trim().charAt(0).toUpperCase() + str.trim().slice(1) : '';

const ProposeTopicDialog = ({
  open,
  onClose,
  topics = [],
  institutionId,
  onSubmitted,
  onSuccess,
}) => {
  const [topicId, setTopicId] = useState('');
  const [newTopic, setNewTopic] = useState('');
  const [subtopic, setSubtopic] = useState('');
  const [pending, setPending] = useState([]);
  const [topicError, setTopicError] = useState('');
  const [subtopicError, setSubtopicError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const selectedTopic =
    topics.find((tp) => String(tp.id) === String(topicId)) || {};
  const existingSubs = selectedTopic.subtopics?.map((s) => s.name) || [];
  const pendingSubs = pending
    .filter((p) => {
      const matchTopic = p.topic
        ? String(p.topic) === String(topicId)
        : (p.topic_name || '').trim().toLowerCase() ===
          (topicId === 'new'
            ? newTopic.trim().toLowerCase()
            : (selectedTopic.name || '').trim().toLowerCase());
      return matchTopic;
    })
    .map((p) => `${p.subtopic_name} (pending)`);
  const subtopicList = [...existingSubs, ...pendingSubs];

  useEffect(() => {
    if (open) {
      setTopicId('');
      setNewTopic('');
      setSubtopic('');
      setTopicError('');
      setSubtopicError('');
      fetchTopicProposals(institutionId)
        .then((res) => setPending(res.results || res))
        .catch(() => setPending([]));
    }
  }, [open, institutionId]);

  useEffect(() => {
    if (!open) return;
    if (topicId !== 'new') {
      setTopicError('');
      return;
    }
    const name = newTopic.trim().toLowerCase();
    if (!name) {
      setTopicError('');
      return;
    }
    const dupApproved = topics.some(
      (t) => (t.name || '').trim().toLowerCase() === name
    );
    const dupPending = pending.some(
      (p) => (p.topic_name || '').trim().toLowerCase() === name && !p.topic
    );
    setTopicError(
      dupApproved || dupPending ? 'Topic already exists or pending' : ''
    );
  }, [newTopic, topicId, topics, pending, open]);

  useEffect(() => {
    if (!open) return;
    const name = subtopic.trim().toLowerCase();
    if (!name) {
      setSubtopicError('');
      return;
    }
    let dup = false;
    let topicName = '';
    if (topicId === 'new') {
      topicName = newTopic;
    } else {
      const t = topics.find((tp) => String(tp.id) === String(topicId));
      topicName = t?.name || '';
      dup = t?.subtopics?.some(
        (s) => (s.name || '').trim().toLowerCase() === name
      );
    }
    if (!dup) {
      const topicNameNorm = topicName.trim().toLowerCase();
      dup = pending.some((p) => {
        const matchTopic = p.topic
          ? String(p.topic) === String(topicId)
          : (p.topic_name || '').trim().toLowerCase() === topicNameNorm;
        return (
          matchTopic && (p.subtopic_name || '').trim().toLowerCase() === name
        );
      });
    }
    setSubtopicError(dup ? 'Subtopic already exists or pending' : '');
  }, [subtopic, topicId, topics, pending, newTopic, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!subtopic || (topicId === 'new' && !newTopic)) return;
    setSubmitting(true);
    try {
      const payload = {
        institution: institutionId,
        subtopic_name: capitalize(subtopic),
      };
      if (topicId === 'new') {
        payload.topic_name = capitalize(newTopic);
      } else {
        payload.topic = topicId;
      }
      await api.post('/community/topic-proposals/', payload);
      if (onSubmitted) onSubmitted();
      const msg = 'Proposal submitted';
      if (onSuccess) onSuccess(msg); else setSuccessMessage(msg);
      onClose();
    } catch (err) {
      console.error('Failed to submit proposal:', err.response?.data || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ component: 'form', onSubmit: handleSubmit }}
    >
      <DialogTitle>Propose Topic/Subtopic</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
        <FormControl fullWidth>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Topic <span style={{ color: 'red' }}>*</span>
          </Typography>
          <Select value={topicId} onChange={(e) => setTopicId(e.target.value)}>
            <MenuItem value="" disabled>
              Select topic
            </MenuItem>
            {topics.map((t) => (
              <MenuItem key={t.id} value={t.id}>
                {t.name}
              </MenuItem>
            ))}
            {Array.from(new Set(pending.filter((p) => !p.topic).map((p) => p.topic_name))).map((name) => (
              <MenuItem key={`pending-${name}`} value="" disabled>
                {name} (pending)
              </MenuItem>
            ))}
            <MenuItem value="new">Add a new topic</MenuItem>
          </Select>
        </FormControl>
        {topicId === 'new' && (
          <div>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              New Topic Name <span style={{ color: 'red' }}>*</span>
            </Typography>
            <TextField
              value={newTopic}
              onChange={(e) => setNewTopic(e.target.value)}
              fullWidth
              error={!!topicError}
              helperText={topicError}
            />
          </div>
        )}
        <div>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Subtopic Name <span style={{ color: 'red' }}>*</span>
          </Typography>
          <TextField
            value={subtopic}
            onChange={(e) => setSubtopic(e.target.value)}
            fullWidth
            error={!!subtopicError}
            helperText={subtopicError}
          />
          {subtopicList.length > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
              {subtopicList.join(', ')}
            </Typography>
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <OutlinedButton onClick={onClose} text="Cancel" color="success" />
        <FilledButton
          type="submit"
          disabled={
            submitting ||
            !subtopic ||
            !!subtopicError ||
            (topicId === 'new' && (!newTopic || !!topicError)) ||
            (!topicId && topicId !== 'new')
          }
          text="Submit"
          color="success"
        />
      </DialogActions>
    </Dialog>
    <Snackbar
      open={!!successMessage}
      autoHideDuration={3000}
      onClose={() => setSuccessMessage('')}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
    >
      <Alert onClose={() => setSuccessMessage('')} severity="success" sx={{ width: '100%', fontWeight: 'bold' }}>
        {successMessage}
      </Alert>
    </Snackbar>
    </>
  );
};

export default ProposeTopicDialog;
